import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import ChooseAvatarVIcon from '@/images/ChooseAvatarVIcon.svg';
import ChooseAvatarXIcon from '@/images/ChooseAvatarXIcon.svg';
import expressHighQuality from '@/images/expressHighQuality.svg';
import infoCircle from '@/images/InfoCircle.svg';
import premiumPlusHighestQuality from '@/images/premiumPlusHighestQuality.svg';
import RadioCheckedChooseAvatar from '@/images/RadioCheckedChooseAvatar.svg';
import RadioUncheckedChooseAvatar from '@/images/RadioUncheckedChooseAvatar.svg';
import { AvatarType } from '@/pages/avatar/common/types';
import styled from '@emotion/styled';
import { PremiumPlusTitleWithIcon } from './PremiumPlusTitleWithIcon';

const Box = styled.div<{ isSelected: boolean }>`
    border-radius: 12px;
    border: 2px solid ${({ isSelected }) => (isSelected ? 'var(--Color-Primary, #FCA25D)' : 'var(--Blcks-300, #DBDBDB)')};
    background: ${({ isSelected }) => (isSelected ? 'var(--Color-Primary-Light, #FFF8F2)' : 'var(--Color-Greyscale-White, #FFF)')};
    padding: 24px;
    width: 431px;
    height: 269px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    &:hover {
        border-color: var(--Color-Primary, #fca25d);
    }
`;

const Radio = styled.img<{ isSelected: boolean }>`
    position: absolute;
    top: 16px;
    right: 16px;
    min-width: 20px;
    min-height: 20px;
    border-radius: 50%;
    padding: 2px;
    transition: background 0.3s ease;
`;

const FeatureList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
`;

const FeatureItem = styled.li<{ isSupported: boolean }>`
    color: ${({ isSupported }) => (isSupported ? 'var(--Color-Black, #090604)' : 'var(--Color-Black, #5C5C5C)')};
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.32px;

    span {
        display: flex;
        align-items: center;
        position: relative;

        .info-icon {
            position: absolute;
            right: -25px;
            bottom: -2px;
        }
    }
`;

const HighQualityText = styled.span`
    color: var(--Secondary-Primary, #090604);
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.32px;
`;

const BoxDescription = styled.div`
    color: var(--Color-Black, #000);
    font-size: 16px;
    margin-top: 8px;
`;

const Divider = styled.div<{ isSelected: boolean }>`
    width: 100%;
    height: 1px;
    background-color: ${({ isSelected }) => (isSelected ? 'var(--Color-Orange, #FFA500)' : 'var(--Color-Greyscale-300, #DBDBDB)')};
    margin: 16px 0;
    transition: background-color 0.3s ease;
`;

interface BoxProps {
    id: AvatarType.Express | AvatarType.PremiumPlus;
    title: string;
    description: string;
    features: { text: string; isSupported: boolean; shouldHaveInfoCircle: boolean; tooltipDescription?: string }[];
    isSelected: boolean;
    onClick: () => void;
    testId?: string;
}

export const SelectableBox = ({ id, title, features, isSelected, onClick, testId }: BoxProps) => (
    <Box isSelected={isSelected} onClick={onClick} data-testid={testId}>
        <Radio src={isSelected ? RadioCheckedChooseAvatar : RadioUncheckedChooseAvatar} isSelected={isSelected} />
        <PremiumPlusTitleWithIcon title={title} />
        <BoxDescription>
            {id === AvatarType.Express ? (
                <>
                    <div>
                        <strong>1 min</strong> of footage required
                    </div>
                    <div>
                        <strong>30 mins</strong> creation time
                    </div>
                </>
            ) : (
                <>
                    <div>
                        <strong>5 min</strong> of footage required
                    </div>
                    <div>
                        <strong>24 hrs</strong> creation time
                    </div>
                </>
            )}
        </BoxDescription>
        <Divider isSelected={isSelected} />
        <FeatureList>
            {features.map(({ text, isSupported, shouldHaveInfoCircle, tooltipDescription }) => (
                <FeatureItem key={text} isSupported={isSupported}>
                    {text === 'High quality' ? (
                        <>
                            <MaskedIcon
                                src={id === AvatarType.Express ? expressHighQuality : premiumPlusHighestQuality}
                                size="24px"
                                color={id === AvatarType.Express ? '#000' : '#FF882E'}
                                style={{ marginRight: '8px' }}
                            />
                            <HighQualityText>{text}</HighQualityText>
                        </>
                    ) : (
                        <>
                            <MaskedIcon
                                src={isSupported ? ChooseAvatarVIcon : ChooseAvatarXIcon}
                                size="24px"
                                color={isSupported ? '#FF882E' : '#000'}
                                style={{ marginRight: '8px' }}
                            />
                            <span>{text}</span>
                        </>
                    )}
                    {shouldHaveInfoCircle && (
                        <span className="info-icon-wrapper" style={{ marginLeft: '8px' }}>
                            <CustomTooltip title={tooltipDescription} fitContent={true}>
                                <MaskedIcon src={infoCircle} size="16px" />
                            </CustomTooltip>
                        </span>
                    )}
                </FeatureItem>
            ))}
        </FeatureList>
    </Box>
);
